/**
 * (c) 2023, Center for Computational Thinking and Design at Aarhus University and contributors
 * Modifications (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  DataConnectionStep,
  isDataConnectionDialogOpen,
  useDataConnectionActions,
} from "../data-connection-flow";
import microbitImage from "../images/stylised-microbit-black.svg";
import { keyboardShortcuts, useShortcut } from "../keyboard-shortcut-hooks";
import { useLogging } from "../logging/logging-hooks";
import { dataConnectionTypeToAnalyticsTransport } from "../logging/step-tracking";
import { TrainModelDialogStage } from "../model";
import { Button, css, cx, HStack, Image, Text, VStack } from "@microbit/ui";
import { useStore } from "../store";
import { tourElClassname } from "../tours";
import AlertIcon from "./AlertIcon";
import InfoToolTip from "./InfoToolTip";
import LiveGraph from "./LiveGraph";
import PredictedAction from "./PredictedAction";

interface LiveGraphPanelProps {
  showPredictedAction?: boolean;
  showDisconnectedOverlay?: boolean;
  disconnectedTextId: string;
}

export const predictedActionDisplayWidth = 180;

const LiveGraphPanel = ({
  showPredictedAction,
  disconnectedTextId,
  showDisconnectedOverlay = true,
}: LiveGraphPanelProps) => {
  const actions = useDataConnectionActions();
  const dataConnection = useStore((s) => s.dataConnection);
  const isTraining = useStore(
    (s) =>
      s.trainModelDialogStage === TrainModelDialogStage.Help ||
      s.trainModelDialogStage === TrainModelDialogStage.TrainingInProgress
  );
  const isConnected = dataConnection.step === DataConnectionStep.Connected;
  const isReconnecting = dataConnection.isReconnecting;
  const logging = useLogging();

  // Show disconnected state when not connected, not reconnecting, and don't distract from dialogs.
  const isDisconnected =
    !isConnected &&
    !isReconnecting &&
    !isDataConnectionDialogOpen(dataConnection.step);

  const handleConnectOrReconnect = useCallback(() => {
    actions.connect();
  }, [actions]);
  useShortcut(keyboardShortcuts.connect, handleConnectOrReconnect, {
    enabled: isDisconnected,
  });
  const handleDisconnect = useCallback(() => {
    logging.event({
      type: "device_disconnect",
      detail: {
        reason: "user",
        transport: dataConnectionTypeToAnalyticsTransport(dataConnection.type),
      },
    });
    void actions.disconnect();
  }, [actions, dataConnection.type, logging]);
  useShortcut(keyboardShortcuts.disconnect, handleDisconnect, {
    enabled: isConnected,
  });
  const intl = useIntl();
  return (
    <HStack
      role="region"
      aria-label={intl.formatMessage({ id: "data-connection-region" })}
      position="relative"
      isolation="isolate"
      h="160px"
      width="100%"
      bg="white"
      className={tourElClassname.liveGraph}
    >
      {isDisconnected && showDisconnectedOverlay && (
        <HStack
          position="absolute"
          w="100%"
          h="100%"
          gap={10}
          justifyContent="center"
          zIndex={2}
          // On mobile text will overlap
          bg={{ base: "white", md: "unset" }}
        >
          <MicrobitWarningIllustration
            className={css({ display: { base: "none", sm: "block" } })}
          />
          <VStack gap={3} alignItems="self-start">
            <Text fontWeight="bold">
              <FormattedMessage id="microbit-not-connected" />
            </Text>
            <Text>
              <FormattedMessage id={disconnectedTextId} />
            </Text>
            <Button
              variant="primary"
              onPress={handleConnectOrReconnect}
              aria-label={intl.formatMessage({ id: "connect-action-aria" })}
            >
              <FormattedMessage id="connect-action" />
            </Button>
          </VStack>
        </HStack>
      )}
      <HStack
        className={cx(
          isDisconnected
            ? css({ pointerEvents: "none", opacity: 0.2 })
            : undefined
        )}
        // Nothing behind the "not connected" overlay should be reachable.
        // React 18 neither types nor coerces `inert`, hence the HTML boolean
        // form.
        {...(isDisconnected ? ({ inert: "" } as { inert?: string }) : {})}
      >
        <HStack
          position="absolute"
          zIndex={1}
          top={0}
          left={0}
          right={0}
          px={5}
          py={2.5}
          style={{
            width: `calc(100% - ${
              showPredictedAction ? predictedActionDisplayWidth : 0
            }px)`,
          }}
        >
          <HStack gap={4}>
            <HStack gap={2}>
              <Text fontWeight="bold">
                <FormattedMessage id="live-data-graph" />
              </Text>
              <InfoToolTip
                titleId="live-graph"
                descriptionId="live-graph-tooltip"
              />
            </HStack>
            {isConnected && (
              <Button
                variant="secondary"
                size="xs"
                onPress={handleDisconnect}
                css={{ backgroundColor: "white" }}
              >
                <FormattedMessage id="disconnect-action" />
              </Button>
            )}
            {isReconnecting && (
              <Text bg="white" fontWeight="bold">
                <FormattedMessage id="reconnecting" />
              </Text>
            )}
          </HStack>
        </HStack>
        <HStack position="absolute" width="100%" height="100%" gap={0}>
          <LiveGraph paused={isTraining} />
          {showPredictedAction && <PredictedAction />}
        </HStack>
      </HStack>
    </HStack>
  );
};

const MicrobitWarningIllustration = ({ className }: { className?: string }) => (
  <HStack position="relative" aria-hidden className={className}>
    <Image src={microbitImage} objectFit="contain" boxSize="120px" bottom={0} />
    <AlertIcon
      className={css({
        position: "absolute",
        top: -1,
        right: -5,
        boxSize: "55px",
        fill: "#ffde21",
      })}
    />
  </HStack>
);

export default LiveGraphPanel;
